import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { webPubSubNegotiateUrl } from "api/endpoints";
import { buildWebPubSubNegotiatePayload } from "utils/userIdentity";
import { showSystemNotification } from "utils/systemNotifications";

const DEFAULT_GROUP = "kpi_alerts";
const DEFAULT_GROUPS = [
  DEFAULT_GROUP,
  "environmental_alerts",
  "activity_alerts",
  "kpi_alerts_green",
  "kpi_alerts_amber",
  "kpi_alerts_red",
  "environmental_alerts_green",
  "environmental_alerts_amber",
  "environmental_alerts_red",
  "activity_alerts_green",
  "activity_alerts_amber",
  "activity_alerts_red",
];
const DEFAULT_MAX_ALERTS = 25;
const WEB_PUBSUB_SUBPROTOCOL = "json.webpubsub.azure.v1";
const GLOBAL_MAX_ALERTS = 250;
const ALERT_STREAM_ENABLED = true;

let sharedAlerts = [];
const alertSubscribers = new Set();
const suppressedAlertSets = new Map();

const notifyAlertSubscribers = () => {
  const snapshot = sharedAlerts;
  alertSubscribers.forEach((callback) => {
    try {
      callback(snapshot);
    } catch (error) {
      console.error("Alert subscriber callback failed", error);
    }
  });
};

const appendSharedAlert = (alert) => {
  sharedAlerts = [alert, ...sharedAlerts].slice(0, GLOBAL_MAX_ALERTS);
  notifyAlertSubscribers();
};

const replaceSharedAlerts = (next) => {
  sharedAlerts = Array.isArray(next) ? next.slice(0, GLOBAL_MAX_ALERTS) : [];
  notifyAlertSubscribers();
};

const safeJsonParse = (value) => {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn("Failed to parse JSON payload from WebPubSub", error, value);
    return null;
  }
};

const normalizeIncomingAlert = (envelope) => {
  if (!envelope || envelope.type !== "message") return null;

  const { data, dataType, group, from, sequenceId } = envelope;
  const receivedAt = new Date().toISOString();

  const attemptObject = () => {
    if (dataType === "json") return data;
    if (typeof data === "string") return safeJsonParse(data) ?? data;
    if (data && typeof data === "object") return data;
    return null;
  };

  const payload = attemptObject();

  const randomSuffix = Math.floor(Math.random() * 1000000);
  const baseId = sequenceId ?? Date.now();

  if (typeof payload === "string") {
    return {
      id: `${baseId}-${randomSuffix}`,
      title: payload,
      severity: "info",
      group: group ?? DEFAULT_GROUP,
      from: from ?? null,
      receivedAt,
      raw: envelope,
    };
  }

  if (payload && typeof payload === "object") {
    const title =
      payload.title ?? payload.message ?? payload.body ?? payload.description ?? "Alert received";
    const detail = payload.message ?? payload.body ?? payload.description ?? payload.detail;
    const severityRaw = payload.severity ?? payload.level ?? payload.status ?? "info";
    const severity = typeof severityRaw === "string" ? severityRaw.toLowerCase() : "info";
    const display = detail && detail !== title ? `${title}: ${detail}` : title;

    return {
      id: `${baseId}-${randomSuffix}`,
      title: typeof display === "string" ? display : JSON.stringify(display),
      severity,
      group: group ?? DEFAULT_GROUP,
      from: from ?? null,
      receivedAt,
      raw: { envelope, payload },
    };
  }

  if (data != null) {
    return {
      id: `${baseId}-${randomSuffix}`,
      title: typeof data === "string" ? data : JSON.stringify(data),
      severity: "info",
      group: group ?? DEFAULT_GROUP,
      from: from ?? null,
      receivedAt,
      raw: envelope,
    };
  }

  return null;
};

export default function useWebPubSubAlerts(options = {}) {
  const {
    group: groupOption = DEFAULT_GROUP,
    groups: groupsProp = DEFAULT_GROUPS,
    autoJoin = true,
    enabled = true,
    maxAlerts = DEFAULT_MAX_ALERTS,
    instanceKey = null,
  } = options;
  const maxAlertsRef = useRef(Math.max(1, maxAlerts));
  const suppressedSetRef = useRef(null);

  if (suppressedSetRef.current == null) {
    if (instanceKey != null) {
      if (!suppressedAlertSets.has(instanceKey)) {
        suppressedAlertSets.set(instanceKey, new Set());
      }
      suppressedSetRef.current = suppressedAlertSets.get(instanceKey);
    } else {
      suppressedSetRef.current = new Set();
    }
  }

  const { groupsToJoin, groupedLookup } = useMemo(() => {
    const seen = new Set();
    const list = [];

    const append = (value) => {
      if (value == null) return;
      const str = `${value}`.trim();
      if (!str || seen.has(str)) return;
      seen.add(str);
      list.push(str);
    };

    if (Array.isArray(groupsProp)) {
      groupsProp.forEach(append);
    }

    append(groupOption);

    if (list.length === 0) {
      DEFAULT_GROUPS.forEach(append);
    }

    return { groupsToJoin: list, groupedLookup: new Set(list) };
  }, [groupOption, groupsProp]);

  const primaryGroup = groupsToJoin[0] ?? DEFAULT_GROUP;
  const groupsKey = groupsToJoin.join("|");

  const [alerts, setAlerts] = useState(() => sharedAlerts.slice(0, maxAlertsRef.current));
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    maxAlertsRef.current = Math.max(1, maxAlerts);

    const handleSharedUpdate = (snapshot) => {
      const active = Array.isArray(snapshot) ? snapshot : [];
      const activeIds = new Set();
      const filtered = [];

      active.forEach((alert) => {
        activeIds.add(alert.id);
        if (!suppressedSetRef.current || !suppressedSetRef.current.has(alert.id)) {
          if (filtered.length < maxAlertsRef.current) {
            filtered.push(alert);
          }
        }
      });

      // prune suppressed ids that no longer exist in the shared list
      if (suppressedSetRef.current) {
        const suppressed = suppressedSetRef.current;
        suppressed.forEach((id) => {
          if (!activeIds.has(id)) {
            suppressed.delete(id);
          }
        });
      }

      setAlerts(filtered);
    };

    alertSubscribers.add(handleSharedUpdate);
    handleSharedUpdate(sharedAlerts);

    return () => {
      alertSubscribers.delete(handleSharedUpdate);
    };
  }, [maxAlerts]);

  useEffect(() => {
    let cancelled = false;
    let attemptCount = 0;

    const stopReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeSocket = () => {
      if (socketRef.current) {
        try {
          socketRef.current.close(1000, "component unmounted");
        } catch (closeError) {
          console.warn("Error closing WebSocket", closeError);
        }
        socketRef.current = null;
      }
    };

    const cleanup = () => {
      cancelled = true;
      stopReconnectTimer();
      closeSocket();
    };

    if (!enabled || !ALERT_STREAM_ENABLED) {
      setStatus("idle");
      cleanup();
      return cleanup;
    }

    const scheduleReconnect = () => {
      if (cancelled || !enabled || !ALERT_STREAM_ENABLED) return;
      stopReconnectTimer();
      attemptCount += 1;
      const cappedAttempt = Math.min(attemptCount, 5);
      const delay = Math.min(30000, 1000 * 2 ** (cappedAttempt - 1));
      reconnectTimerRef.current = setTimeout(() => {
        if (cancelled || !enabled || !ALERT_STREAM_ENABLED) return;
        connect();
      }, delay);
    };

    const handleMessage = (event) => {
      if (cancelled) return;
      const envelope = safeJsonParse(event.data);
      if (typeof window !== "undefined") {
        window.__omnioAlertsDebug = {
          ...(window.__omnioAlertsDebug || {}),
          lastEnvelope: envelope,
        };
      }
      if (!envelope) {
        if (typeof window !== "undefined") {
          window.__omnioAlertsDebug = {
            ...(window.__omnioAlertsDebug || {}),
            lastError: "Unable to parse WebPubSub envelope",
          };
        }
        return;
      }
      const alert = normalizeIncomingAlert(envelope);
      if (!alert) {
        if (typeof window !== "undefined") {
          window.__omnioAlertsDebug = {
            ...(window.__omnioAlertsDebug || {}),
            lastIgnoredEnvelope: envelope,
          };
        }
        return;
      }
      if (groupedLookup.size && alert.group && !groupedLookup.has(alert.group)) return;
      appendSharedAlert(alert);
      showSystemNotification(alert);
      if (typeof window !== "undefined") {
        window.__omnioAlertsDebug = {
          ...(window.__omnioAlertsDebug || {}),
          lastAlert: alert,
        };
      }
    };

    async function connect() {
      if (cancelled || !enabled || !ALERT_STREAM_ENABLED) return;
      stopReconnectTimer();
      closeSocket();
      setStatus(attemptCount === 0 ? "connecting" : "reconnecting");
      setError(null);

      try {
        const negotiateUrl = webPubSubNegotiateUrl();
        if (!negotiateUrl) {
          setStatus("idle");
          return;
        }
        const negotiatePayload = buildWebPubSubNegotiatePayload(primaryGroup, groupsToJoin);
        const response = await axios.post(negotiateUrl, negotiatePayload, {
          headers: { "Content-Type": "application/json" },
        });
        if (cancelled || !enabled) return;

        const { url } = response.data ?? {};
        if (!url) throw new Error("WebPubSub negotiate response did not include a url");

        const socket = new WebSocket(url, WEB_PUBSUB_SUBPROTOCOL);
        socketRef.current = socket;

        socket.addEventListener("open", () => {
          if (cancelled) return;
          attemptCount = 0;
          setStatus("connected");
          if (typeof window !== "undefined") {
            window.__omnioAlertsDebug = {
              ...(window.__omnioAlertsDebug || {}),
              connectedAt: new Date().toISOString(),
              groups: groupsToJoin,
            };
          }
          if (autoJoin && groupsToJoin.length > 0) {
            groupsToJoin.forEach((targetGroup) => {
              try {
                socket.send(JSON.stringify({ type: "joinGroup", group: targetGroup }));
              } catch (sendError) {
                console.warn("Failed to join WebPubSub group", targetGroup, sendError);
              }
            });
          }
        });

        socket.addEventListener("message", handleMessage);

        socket.addEventListener("close", () => {
          if (cancelled) return;
          setStatus("disconnected");
          if (typeof window !== "undefined") {
            window.__omnioAlertsDebug = {
              ...(window.__omnioAlertsDebug || {}),
              disconnectedAt: new Date().toISOString(),
            };
          }
          scheduleReconnect();
        });

        socket.addEventListener("error", (socketError) => {
          if (cancelled) return;
          console.error("WebPubSub socket error", socketError);
          setError(socketError instanceof Error ? socketError : new Error("WebSocket error"));
          if (typeof window !== "undefined") {
            window.__omnioAlertsDebug = {
              ...(window.__omnioAlertsDebug || {}),
              lastSocketError:
                socketError instanceof Error ? socketError.message : "WebSocket error",
            };
          }
        });
      } catch (connectionError) {
        if (cancelled) return;
        console.error("Failed to negotiate WebPubSub connection", connectionError);
        setError(
          connectionError instanceof Error ? connectionError : new Error("Connection error")
        );
        setStatus("error");
        if (typeof window !== "undefined") {
          window.__omnioAlertsDebug = {
            ...(window.__omnioAlertsDebug || {}),
            lastNegotiateError:
              connectionError instanceof Error ? connectionError.message : "Connection error",
          };
        }
        scheduleReconnect();
      }
    }

    connect();

    return cleanup;
  }, [autoJoin, enabled, groupsKey, maxAlerts, primaryGroup, groupedLookup]);

  const clearAlerts = () => {
    const latest = sharedAlerts;
    const suppressed = suppressedSetRef.current;
    if (suppressed) {
      latest.forEach((alert) => {
        if (alert?.id) {
          suppressed.add(alert.id);
        }
      });
    }
    setAlerts([]);
  };

  const clearAllAlerts = () => {
    suppressedAlertSets.forEach((set) => set.clear());
    if (suppressedSetRef.current) {
      suppressedSetRef.current.clear();
    }
    replaceSharedAlerts([]);
  };

  return {
    alerts,
    latestAlert: alerts.length ? alerts[0] : null,
    status,
    error,
    clearAlerts,
    clearAllAlerts,
  };
}
